import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment-timezone";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import { getLogs } from "../services/logsService";

const mapDeliveryStatusToJobStatus = (deliveryStatus) => {
  const statusMap = {
    delivered: "PROCESS_COMPLETED",
    failed: "EMAIL_FAILED",
    processing: "UPLOADED",
  };
  return statusMap[deliveryStatus?.toLowerCase()] || "UPLOADED";
};

const extractJobStatuses = (logs) => {
  const jobStatuses = [...new Set(logs.map((item) => item.job_status))];
  return jobStatuses.map((status) => ({
    name: status,
    value: status,
  }));
};

export const getInitialDateRange = () => {
  const initialStartDate = moment
    .tz(new Date(), "America/Los_Angeles")
    .subtract(30, "day");
  return {
    startDate: initialStartDate.toDate().toISOString(),
    endDate: new Date().toISOString(),
  };
};

const initialDateRange = getInitialDateRange();

export const fetchLogsThunk = createAsyncThunk(
  "logs/fetchLogs",
  async (payload = {}, { getState, rejectWithValue }) => {
    try {
      const clientId =
        typeof payload === "object"
          ? Number(payload?.clientId) || getRequiredCurrentUserClientId(getState())
          : Number(payload) || getRequiredCurrentUserClientId(getState());
      const deliveryStatus =
        typeof payload === "object" ? payload?.deliveryStatus || "" : "";
      const pageSize =
        typeof payload === "object" && Number.isFinite(Number(payload?.limit))
          ? Number(payload.limit)
          : 100;

      let offset = 0;
      let total = null;
      let pageCount = 0;
      const batches = [];

      while (true) {
        const response = await getLogs(clientId, {
          limit: pageSize,
          offset,
          deliveryStatus,
        });

        if (response?.error) {
          return rejectWithValue(response.error.message || "Unable to load logs.");
        }

        const logsBatch = Array.isArray(response?.logs) ? response.logs : [];
        batches.push(...logsBatch);

        total = Number.isFinite(Number(response?.total))
          ? Number(response.total)
          : total;

        offset += logsBatch.length;
        pageCount += 1;

        const reachedTotal = Number.isFinite(total) ? offset >= total : false;
        const isLastPage = logsBatch.length < pageSize;
        const safetyStop = pageCount >= 100;

        if (logsBatch.length === 0 || reachedTotal || isLastPage || safetyStop) {
          break;
        }
      }
      const seenIds = new Set();
      const apiLogs = batches.filter((log) => {
        const key = String(log?.id || `${log?.job_name || ""}-${log?.created_at || ""}`);
        if (seenIds.has(key)) {
          return false;
        }
        seenIds.add(key);
        return true;
      });

      const mappedLogs = apiLogs.map((log) => ({
        id: log.id,
        client_id: Number(log.client_id || 0),
        job_name: log.job_name || log.id?.toString() || "",
        email_from_name: log.email_from_name || log.username || "",
        email_from_address: log.email_from_address || log.caller_id || "",
        to_email_addresses: log.to_email_addresses || log.deliver_to || "",
        email_subject:
          log.email_subject || `Voicemail from ${log.caller_id || "Unknown"}`,
        // Prefer backend lifecycle status; fallback to delivery-status mapping for legacy payloads.
        job_status:
          log.job_status || mapDeliveryStatusToJobStatus(log.delivery_status),
        delivery_status: String(log.delivery_status || "").toLowerCase(),
        email_status: log.email_status || "",
        created_time:
          log.created_at || log.delivery_timestamp || new Date().toISOString(),
        delivery_timestamp: log.delivery_timestamp || "",
        sms_delivery_status: String(log.sms_delivery_status || ""),
        sms_delivery_timestamp: log.sms_delivery_timestamp || "",
        duration_ms:
          log.duration_ms === null || log.duration_ms === undefined
            ? null
            : Number(log.duration_ms),
        updated_at: log.updated_at || "",
        _original: log,
      }));

      const sortedLogs = [...mappedLogs].sort((a, b) => {
        const dateA = new Date(a.created_time);
        const dateB = new Date(b.created_time);
        return dateB - dateA;
      });

      return {
        logs: sortedLogs,
        jobStatusOptions: [
          { name: "Job Status", value: "" },
          ...extractJobStatuses(sortedLogs),
        ],
      };
    } catch {
      return rejectWithValue("Unable to load logs.");
    }
  },
);

const initialState = {
  logs: [],
  filteredLogs: [],
  isLoading: false,
  currentPage: 0,
  showResendMessage: {
    status: false,
    message: "",
  },
  startDate: initialDateRange.startDate,
  endDate: initialDateRange.endDate,
  jobStatusOptions: [{ name: "Job Status", value: "" }],
  deliveryStatusFilter: "",
  searchQuery: "",
  dateRange: "all",
  spreadTable: false,
  showFilterModal: false,
  error: null,
  hasLoaded: false,
};

const logsSlice = createSlice({
  name: "logs",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setShowResendMessage: (state, action) => {
      state.showResendMessage = action.payload;
    },
    clearShowResendMessage: (state) => {
      state.showResendMessage = { status: false, message: "" };
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload
        ? new Date(action.payload).toISOString()
        : null;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload
        ? new Date(action.payload).toISOString()
        : null;
    },
    setDeliveryStatusFilter: (state, action) => {
      state.deliveryStatusFilter = action.payload;
      state.currentPage = 0;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.currentPage = 0;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
      state.currentPage = 0;
    },
    setSpreadTable: (state, action) => {
      state.spreadTable = Boolean(action.payload);
    },
    setShowFilterModal: (state, action) => {
      state.showFilterModal = action.payload;
    },
    resetLogFilters: (state) => {
      const nextRange = getInitialDateRange();
      state.startDate = nextRange.startDate;
      state.endDate = nextRange.endDate;
      state.deliveryStatusFilter = "";
      state.searchQuery = "";
      state.dateRange = "all";
      state.currentPage = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLogsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLogsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.logs = action.payload.logs;
        state.filteredLogs = action.payload.logs;
        state.jobStatusOptions = action.payload.jobStatusOptions || [
          { name: "Job Status", value: "" },
        ];
        state.hasLoaded = true;
      })
      .addCase(fetchLogsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.logs = [];
        state.filteredLogs = [];
        state.error = action.payload || "Unable to load logs.";
        state.hasLoaded = true;
      });
  },
});

export const {
  clearShowResendMessage,
  resetLogFilters,
  setCurrentPage,
  setDateRange,
  setDeliveryStatusFilter,
  setEndDate,
  setSearchQuery,
  setShowFilterModal,
  setShowResendMessage,
  setSpreadTable,
  setStartDate,
} = logsSlice.actions;

export const selectLogs = (state) => state.logs.logs;
export const selectFilteredLogs = (state) => state.logs.filteredLogs;
export const selectLogsLoading = (state) => state.logs.isLoading;
export const selectLogsCurrentPage = (state) => state.logs.currentPage;
export const selectShowResendMessage = (state) => state.logs.showResendMessage;
export const selectLogsStartDate = (state) => state.logs.startDate;
export const selectLogsEndDate = (state) => state.logs.endDate;
export const selectJobStatusOptions = (state) => state.logs.jobStatusOptions;
export const selectDeliveryStatusFilter = (state) => state.logs.deliveryStatusFilter;
export const selectLogsSearchQuery = (state) => state.logs.searchQuery;
export const selectLogsDateRange = (state) => state.logs.dateRange;
export const selectLogsSpreadTable = (state) => state.logs.spreadTable;
export const selectLogsShowFilterModal = (state) => state.logs.showFilterModal;
export const selectLogsError = (state) => state.logs.error;
export const selectLogsHasLoaded = (state) => state.logs.hasLoaded;

export const logsInitialState = initialState;

export default logsSlice.reducer;
